import { useEffect, useRef, useState } from 'react'

interface FrameBudgetOptions {
  /** A frame slower than this (ms) counts as over budget. */
  thresholdMs?: number
  /** This many consecutive slow frames trip the pause. */
  consecutive?: number
  /** How long to hold the pause before re-testing. */
  holdMs?: number
}

/**
 * Frame-budget monitor for the ambient layer (R17): watches rAF deltas
 * while `active`; N consecutive frames over the threshold set `paused`
 * for holdMs, then re-test. The wiring ships in U2 — thresholds are
 * provisional and get tuned against real hardware in the U13 perf pass.
 */
export function useFrameBudgetPause(active: boolean, opts: FrameBudgetOptions = {}): boolean {
  const { thresholdMs = 50, consecutive = 8, holdMs = 10_000 } = opts
  const [paused, setPaused] = useState(false)
  const holdTimer = useRef<number | null>(null)

  useEffect(() => {
    if (!active || paused) return
    let raf = 0
    let last = performance.now()
    let slowRun = 0

    const tick = (now: number) => {
      const delta = now - last
      last = now
      // Deltas over a second mean the tab was hidden or the debugger was
      // open — not real jank; don't count them.
      if (delta < 1000) {
        if (delta > thresholdMs) {
          slowRun += 1
          if (slowRun >= consecutive) {
            setPaused(true)
            return
          }
        } else {
          slowRun = 0
        }
      }
      raf = requestAnimationFrame(tick)
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [active, paused, thresholdMs, consecutive])

  useEffect(() => {
    if (!paused) return
    holdTimer.current = window.setTimeout(() => setPaused(false), holdMs)
    return () => {
      if (holdTimer.current) window.clearTimeout(holdTimer.current)
    }
  }, [paused, holdMs])

  return paused
}
